/** Normalize spacing without changing words (trim ends, collapse 3+ newlines to 2). */
export function normalizeTranscriptWhitespace(text: string): string {
  return text.replace(/\n{3,}/g, "\n\n").trim();
}

/** Product, site, delivery, and customer-deal terms — boosts retention scores. */
const INFO_KEYWORD_PATTERN = new RegExp(
  [
    "\\bdelivery\\b",
    "\\binstall(?:ation)?\\b",
    "\\bsite(?:\\s+prep)?\\b",
    "\\bquote\\b",
    "\\bpric(?:e|ing)\\b",
    "\\bpayment\\b",
    "\\bwarranty\\b",
    "\\bdeposit\\b",
    "\\borders?\\b",
    "\\bmodel\\b",
    "\\bsize\\b",
    "\\bdimension\\b",
    "\\bcolor\\b",
    "\\bshed\\b",
    "\\bbuilding\\b",
    "\\bgarage\\b",
    "\\b(?:carport|barn)\\b",
    "\\brent(?:\\s+to\\s+own)?\\b",
    "\\brto\\b",
    "\\bfinanc(?:e|ing)\\b",
    "\\bmonthly\\b",
    "\\bdown\\s+payment\\b",
    "\\bpermit\\b",
    "\\bhoa\\b",
    "\\bzoning\\b",
    "\\bproperty\\b",
    "\\blot\\b",
    "\\baddress\\b",
    "\\bzip(?:\\s+code)?\\b",
    "\\bcounty\\b",
    "\\blocation\\b",
    "\\bdirections?\\b",
    "\\baccess\\b",
    "\\bgate\\b",
    "\\beasement\\b",
    "\\bsurvey\\b",
    "\\bfoundation\\b",
    "\\bpad\\b",
    "\\bgravel\\b",
    "\\blevel\\b",
    "\\bschedule\\b",
    "\\bappointment\\b",
    "\\bwindow\\b",
    "\\bdoor\\b",
    "\\broof\\b",
    "\\b(?:metal|vinyl|steel|wood)\\b",
    "\\bsq\\.?\\s*ft\\b",
    "\\bsquare\\s+foot",
    "\\bfootprint\\b",
    "\\bcustomer\\b",
    "\\bcontract\\b",
    "\\binvoice\\b",
    "\\blead\\s*time\\b",
    "\\bprep\\b",
    "\\bacre\\b",
    "\\bbackyard\\b",
    "\\bresidential\\b",
    "\\bworkshop\\b",
    "\\bstorage\\b",
    "\\buse\\s+case\\b",
    "\\bwhere\\s+it('?ll| will) go\\b",
  ].join("|"),
  "gi"
);

const FLUFF_ONLY_LINE =
  /^(yeah|yep|yes|no|no+\s?pe|ok+|okay|uh+\s*huh|mhm+|mm+|hmm+|right|sure|i\s+see|got\s+it|cool|great|awesome|nice|thanks|thank\s+you|appreciate\s+it|sounds\s+good|makes\s+sense|go\s+ahead|of\s+course|no\s+problem|you\s+bet|alright|all\s+right)\s*[.!?…]*$/i;

/** Phone, money, email, address-ish, dimensions — never drop blocks containing these. */
export function hasStructuredSalesSignals(text: string): boolean {
  const t = text;
  if (/\$[\d,]+(?:\.\d{2})?\b/.test(t)) return true;
  if (/\b\d{3}[-.\s]?\d{3}[-.\s]?\d{4}\b/.test(t)) return true;
  if (/[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i.test(t)) return true;
  if (/\b\d{5}(?:-\d{4})?\b/.test(t)) return true;
  if (/\b\d{1,5}\s+[a-z0-9'.\s]+\s+(st|street|rd|road|ave|avenue|dr|drive|ln|lane|way|blvd|circle|ct|court)\b/i.test(t))
    return true;
  if (/\b\d{2,4}\s*(?:x|by|×)\s*\d{2,4}\b/i.test(t)) return true;
  if (/\b\d+\s*(?:sq\.?\s*ft|sqft|square\s+feet|ft²)\b/i.test(t)) return true;
  return false;
}

function scoreLine(line: string): number {
  const t = line.trim();
  if (!t.length) return 0;
  let s = 0;
  if (hasStructuredSalesSignals(t)) return 100;
  const digitRatio = (t.match(/\d/g) ?? []).length / t.length;
  if (digitRatio > 0.045) s += 28;
  else if (digitRatio > 0.02) s += 14;

  const kw = (t.match(INFO_KEYWORD_PATTERN) ?? []).length;
  s += Math.min(36, kw * 9);

  const words = t.split(/\s+/).filter(Boolean);
  if (words.length >= 6) s += Math.min(14, Math.floor(words.length / 4));

  if (t.length < 48 && FLUFF_ONLY_LINE.test(t)) s = Math.min(s, 8);
  if (t.length < 28 && /^[a-z\s.'!?…]+$/i.test(t) && !/\d/.test(t)) s = Math.min(s, 12);

  return Math.min(100, s);
}

function scoreBlock(text: string): number {
  const lines = text.split(/\n/).map((l) => l.trim()).filter((l) => l.length > 0);
  if (!lines.length) return 0;
  if (hasStructuredSalesSignals(text)) return 100;
  const lineScores = lines.map(scoreLine);
  const max = Math.max(...lineScores);
  const avg = lineScores.reduce((a, b) => a + b, 0) / lineScores.length;
  return Math.round(max * 0.55 + avg * 0.45);
}

/** Very short blocks are kept (sign-offs, single facts). */
const SHORT_BLOCK_KEEP = 85;
/** Minimum size before a block can be treated as fluff (must also score low). */
const MIN_FLUFF_BLOCK_CHARS = 220;
/** Minimum combined size to drop a run of two or more fluff blocks. */
const MIN_FLUFF_RUN_CHARS = 300;
/** Drop a single isolated fluff block at this length or above. */
const MIN_FLUFF_SINGLE_DROP = 280;
const FLUFF_SCORE_AT_OR_BELOW = 22;

function splitDenseBlob(blob: string): string[] {
  const sentences = blob.split(/(?<=[.!?])\s+/).filter((s) => s.trim().length > 0);
  if (sentences.length <= 1) {
    const hard = blob.match(/[\s\S]{1,480}/g) ?? [blob];
    return hard.map((s) => s.trim()).filter(Boolean);
  }
  const chunks: string[] = [];
  let buf = "";
  for (const s of sentences) {
    const next = buf ? `${buf} ${s}` : s;
    if (next.length > 440 && buf.length > 100) {
      chunks.push(buf.trim());
      buf = s;
    } else {
      buf = next;
    }
  }
  if (buf.trim()) chunks.push(buf.trim());
  return chunks.length ? chunks : [blob.trim()];
}

function splitIntoBlocks(raw: string): string[] {
  const t = raw.replace(/\r\n/g, "\n").trim();
  if (!t) return [];
  let blocks = t.split(/\n\s*\n+/).map((p) => p.trim()).filter((p) => p.length > 0);
  if (blocks.length === 1 && blocks[0].length > 1100) {
    blocks = splitDenseBlob(blocks[0]);
  }
  return blocks;
}

export type RemovedFluffSection = {
  /** First ~160 chars of removed text for summary UI */
  preview: string;
  charCount: number;
};

export function trimFluffSections(raw: string): { text: string; removed: RemovedFluffSection[] } {
  const blocks = splitIntoBlocks(raw);
  if (blocks.length === 0) return { text: "", removed: [] };

  const items = blocks.map((text) => {
    const score = scoreBlock(text);
    const mustKeep =
      hasStructuredSalesSignals(text) || text.length < SHORT_BLOCK_KEEP || score >= 38;
    return { text, score, len: text.length, mustKeep };
  });

  const isFluffCandidate = (b: (typeof items)[0]) =>
    !b.mustKeep && b.score <= FLUFF_SCORE_AT_OR_BELOW && b.len >= MIN_FLUFF_BLOCK_CHARS;

  const drop = new Array(items.length).fill(false);
  let i = 0;
  while (i < items.length) {
    if (!isFluffCandidate(items[i])) {
      i++;
      continue;
    }
    let j = i;
    let runLen = 0;
    while (j < items.length && isFluffCandidate(items[j])) {
      runLen += items[j].len;
      j++;
    }
    const runCount = j - i;
    const shouldRemove =
      runLen >= MIN_FLUFF_RUN_CHARS ||
      (runCount >= 2 && runLen >= 260) ||
      (runCount === 1 && items[i].len >= MIN_FLUFF_SINGLE_DROP);

    if (shouldRemove) {
      for (let k = i; k < j; k++) drop[k] = true;
    }
    i = j;
  }

  const removed: RemovedFluffSection[] = [];
  const kept: string[] = [];
  for (let k = 0; k < items.length; k++) {
    if (drop[k]) {
      const full = items[k].text;
      removed.push({
        preview: full.length > 160 ? `${full.slice(0, 157)}…` : full,
        charCount: full.length,
      });
    } else {
      kept.push(items[k].text);
    }
  }

  return {
    text: kept.join("\n\n"),
    removed,
  };
}

export type TranscriptTrimPreview = {
  trimmed: string;
  summaryLines: string[];
  removedChars: number;
  removedPercent: number;
  /** Samples of cut low-information sections */
  removedFluffSections: RemovedFluffSection[];
};

export function buildTranscriptTrimPreview(original: string): TranscriptTrimPreview {
  const fluff = trimFluffSections(original);
  const trimmed = normalizeTranscriptWhitespace(fluff.text);

  const removedChars = Math.max(0, original.length - trimmed.length);
  const removedPercent = original.length > 0 ? Math.round((removedChars / original.length) * 100) : 0;
  const fluffCutChars = fluff.removed.reduce((a, s) => a + s.charCount, 0);

  if (removedChars === 0 && fluff.removed.length === 0) {
    return {
      trimmed: original,
      summaryLines: ["Nothing to trim — transcript already compact."],
      removedChars: 0,
      removedPercent: 0,
      removedFluffSections: [],
    };
  }

  const summaryLines: string[] = [];

  if (fluff.removed.length > 0) {
    summaryLines.push(
      `Removed ${fluff.removed.length} low-information section${fluff.removed.length === 1 ? "" : "s"} (~${fluffCutChars} characters of conversational filler).`
    );
    summaryLines.push(
      "Kept content that looks like product, pricing, site or delivery, contact or location, dimensions, or other deal-related detail."
    );
  }

  const normOrig = original.replace(/\r\n/g, "\n");
  if (normOrig.trimStart() !== normOrig || normOrig.trimEnd() !== normOrig) {
    summaryLines.push("Trimmed extra blank space at the start or end.");
  }
  if (normOrig.includes("\n\n\n")) {
    summaryLines.push("Collapsed runs of 3+ blank lines to a single paragraph break.");
  }

  summaryLines.push(
    `About ${removedChars} character${removedChars === 1 ? "" : "s"} removed in total (${removedPercent}% of current length).`
  );

  return {
    trimmed,
    summaryLines,
    removedChars,
    removedPercent,
    removedFluffSections: fluff.removed,
  };
}
